import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type NavigationState = {
  subNavCollapsed: boolean;
  manageDealParticipantsCollapsed: boolean;
  sidenavPlaceholdersCollapsed: boolean;
  activeSidenavPlaceholder: string | null;
  activeParticipantsTab: 'counterparty' | 'dealteam' | null;
  toggleSubNavCollapsed: (collapsed: boolean) => void;
  toggleManageDealParticipantsCollapsed: (collapsed: boolean) => void;
  toggleSidenavPlaceholdersCollapsed: (collapsed: boolean) => void;
  setActiveSidenavPlaceholder: (uuid: string | null) => void;
  setActiveParticipantsTab: (tab: 'counterparty' | 'dealteam' | null) => void;
};

const NavigationContext = createContext<NavigationState | null>(null);

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [subNavCollapsed, setSubNavCollapsed] = useState(false);
  const [manageDealParticipantsCollapsed, setManageDealParticipantsCollapsed] = useState(true);
  const [sidenavPlaceholdersCollapsed, setSidenavPlaceholdersCollapsed] = useState(true);
  const [activeSidenavPlaceholder, setActiveSidenavPlaceholderState] = useState<string | null>(null);
  const [activeParticipantsTab, setActiveParticipantsTabState] = useState<'counterparty' | 'dealteam' | null>(null);

  const toggleSubNavCollapsed = useCallback((collapsed: boolean) => {
    setSubNavCollapsed(collapsed);
  }, []);

  const toggleManageDealParticipantsCollapsed = useCallback((collapsed: boolean) => {
    setManageDealParticipantsCollapsed(collapsed);
  }, []);

  const toggleSidenavPlaceholdersCollapsed = useCallback((collapsed: boolean) => {
    setSidenavPlaceholdersCollapsed(collapsed);
  }, []);

  const setActiveSidenavPlaceholder = useCallback((uuid: string | null) => {
    setActiveSidenavPlaceholderState(uuid);
  }, []);

  const setActiveParticipantsTab = useCallback((tab: 'counterparty' | 'dealteam' | null) => {
    setActiveParticipantsTabState(tab);
  }, []);

  return (
    <NavigationContext.Provider
      value={{
        subNavCollapsed,
        manageDealParticipantsCollapsed,
        sidenavPlaceholdersCollapsed,
        activeSidenavPlaceholder,
        activeParticipantsTab,
        toggleSubNavCollapsed,
        toggleManageDealParticipantsCollapsed,
        toggleSidenavPlaceholdersCollapsed,
        setActiveSidenavPlaceholder,
        setActiveParticipantsTab,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationState => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
